import React from 'react';
import PropTypes from 'prop-types';
import { Card } from 'antd';
import {
    CORE_SIZE__SINGLEMODE,
    CORE_SIZE__MULTIMODE_50,
    CORE_SIZE__MULTIMODE_62_5,
    MTJ__SINGLE_END_A,
    MTJ__SINGLE_END_B,
    MTJ__SINGLE_END_A_THEN_END_B,
    MTJ__DOUBLE,
    TERMINATION_TYPE__BARE_FIBER,
} from '@shared/constants/constants';
import './TestPlanSummaryTable.scss';
import { WizardState } from '@shared/types/test-plan/types';

const REQUIRED_POLARITY_EQUIPMENT = {
    NONE : 'NONE',
    PTM : 'PTM-100',
    RD_P : 'RD-P-100',
    RD_SP : 'RD-SP-100'
};

interface TestPlanSummaryTableProps {
    wizard: WizardState,
    requiredEquipment: WizardState['requiredEquipment'],
}

export class TestPlanSummaryTable extends React.PureComponent<TestPlanSummaryTableProps> {
    static propTypes = {
        wizard: PropTypes.object.isRequired,
        requiredEquipment: PropTypes.object.isRequired,
    };

    getRequiredPolarityEquipment() {
        if(this.props.requiredEquipment.pt1 == null) {
            return REQUIRED_POLARITY_EQUIPMENT.NONE;
        }
        else if(this.props.wizard.assembly.polaritySequence === 1 && this.props.wizard.assembly.terminationTypeEndB !== TERMINATION_TYPE__BARE_FIBER) {
            return REQUIRED_POLARITY_EQUIPMENT.PTM;
        }
        else if(this.props.wizard.assembly.polaritySequence === 1 || this.props.wizard.assembly.polaritySequence === 2) {
            return REQUIRED_POLARITY_EQUIPMENT.RD_P;
        }
        else if(this.props.wizard.assembly.polaritySequence === 3) {
            return REQUIRED_POLARITY_EQUIPMENT.RD_SP;
        }

        throw new Error(`Unknown polarity sequence: ${this.props.wizard.assembly.polaritySequence}`);
    }

    render() {
        const requiredPolarityEquipment = this.getRequiredPolarityEquipment();

        return (
            <div className="test-plan-summary-table">
                <table className="spec-table">
                    <tbody>
                        <tr>
                            <td>
                                <div className="title">Recommended Equipment</div>
                            </td>
                            <td>
                                {this.props.requiredEquipment.rl1 !== null && (
                                    <div className="card-style">
                                        <Card title={this.props.requiredEquipment.rl1} size="small">
                                            <img src="/RLM.png" alt="RL1" />
                                        </Card>
                                    </div>
                                )}
                                {this.props.requiredEquipment.rds !== null && (
                                    <div className="card-style">
                                        <Card size="small">
                                            <img src="/RD-S.png" alt="RDS" />
                                        </Card>
                                    </div>
                                )}
                                {this.props.requiredEquipment.switch1 !== null && (
                                    <div className="card-style">
                                        <Card title={this.props.requiredEquipment.switch1} size="small">
                                            <img src="/OSX.png" alt="SX1" />
                                        </Card>
                                    </div>
                                )}
                                {this.props.requiredEquipment.switch2 !== null && (
                                    <div className="card-style">
                                        <Card title={this.props.requiredEquipment.switch2} size="small">
                                            <img src="/OSX.png" alt="SX1" />
                                        </Card>
                                    </div>
                                )}
                                {requiredPolarityEquipment === REQUIRED_POLARITY_EQUIPMENT.PTM && (
                                        <div className="card-style-items">
                                            <Card title={this.props.requiredEquipment.pt1} size="small">
                                                <img src="/PTM.png" alt="PT1" />
                                            </Card>
                                        </div>
                                )}
                                {requiredPolarityEquipment === REQUIRED_POLARITY_EQUIPMENT.RD_P && (
                                        <div className="card-style-items">
                                            <Card title={'RD-P-100'} size="small">
                                                <img src="/RD-S.png" alt="RDS" />
                                            </Card>
                                        </div>
                                )}
                                {requiredPolarityEquipment === REQUIRED_POLARITY_EQUIPMENT.RD_SP && (
                                        <div className="card-style-items">
                                            <Card title={'RD-SP-100'} size="small" >
                                                <img src="/RD-S.png" alt="RDS" />
                                            </Card>
                                        </div>
                                )}
                            </td>
                        </tr>

                        <tr>
                            <td>
                                <div className="title">DUT Config</div>
                            </td>
                            <td>
                                <ul>
                                    {(this.props.wizard.assembly.insertionLossSideA ||
                                        this.props.wizard.assembly.insertionLossSideB ||
                                        this.props.wizard.assembly.returnLossSideA ||
                                        this.props.wizard.assembly.returnLossSideB ||
                                        this.props.wizard.assembly.insertionLossTotal ||
                                        this.props.wizard.assembly.returnLossTotal) && (
                                        <li>
                                            <div className="flex">
                                                <div>Core Size:&nbsp;</div>
                                                <div style={{ fontStyle: 'italic', color: 'blue' }}>
                                                    {this.props.wizard.assembly.coreSize === CORE_SIZE__SINGLEMODE &&
                                                        'Single Mode 9/125'}
                                                    {this.props.wizard.assembly.coreSize === CORE_SIZE__MULTIMODE_50 &&
                                                        'Multi Mode 50/125'}
                                                    {this.props.wizard.assembly.coreSize === CORE_SIZE__MULTIMODE_62_5 &&
                                                        'Multi Mode 62.5/125'}{' '}
                                                    &mu;m
                                                </div>
                                            </div>
                                        </li>
                                    )}
                                    {(this.props.wizard.assembly.insertionLossSideA ||
                                        this.props.wizard.assembly.insertionLossSideB ||
                                        this.props.wizard.assembly.returnLossSideA ||
                                        this.props.wizard.assembly.returnLossSideB ||
                                        this.props.wizard.assembly.insertionLossTotal ||
                                        this.props.wizard.assembly.returnLossTotal) && (
                                        <li>
                                            <div className="flex">
                                                <div>MTJ Configuration:&nbsp;</div>
                                                <div style={{ fontStyle: 'italic', color: 'blue' }}>
                                                    {this.props.wizard.equipment.mtjConfiguration === MTJ__SINGLE_END_A &&
                                                        'Single MTJ (only End A)'}
                                                    {this.props.wizard.equipment.mtjConfiguration === MTJ__SINGLE_END_B &&
                                                        'Single MTJ (only End B)'}
                                                    {this.props.wizard.equipment.mtjConfiguration ===
                                                        MTJ__SINGLE_END_A_THEN_END_B && 'Single MTJ (both Ends)'}
                                                    {this.props.wizard.equipment.mtjConfiguration === MTJ__DOUBLE && 'Dual MTJ'}
                                                </div>
                                            </div>
                                        </li>
                                    )}
                                    <li>
                                        <div className="flex">
                                            <div>Cables Per Assembly:&nbsp;</div>
                                            <div style={{ fontStyle: 'italic', color: 'blue' }}>
                                                {this.props.wizard.cablesPerAssembly}
                                            </div>
                                        </div>
                                    </li>
                                    <li>
                                        <div className="flex">
                                            <div>Fibers Per Cable:&nbsp;</div>
                                            <div style={{ fontStyle: 'italic', color: 'blue' }}>
                                                {this.props.wizard.fibersPerCable}
                                            </div>
                                        </div>
                                    </li>
                                </ul>
                            </td>
                        </tr>
                        {this.props.wizard.assembly.testPolarity && (
                            <tr>
                                <td>
                                    <div className="title">Polarity</div>
                                </td>
                                <td>
                                    <ul>
                                        <li>
                                            <div className="flex">
                                                <div>Cable Mapping:&nbsp;</div>
                                                <div style={{ fontStyle: 'italic', color: 'blue' }}>
                                                    {this.props.wizard.assembly.polarity.name === null
                                                        ? 'Default'
                                                        : this.props.wizard.assembly.polarity.name}
                                                </div>
                                            </div>
                                        </li>
                                    </ul>
                                </td>
                            </tr>
                        )}
                        {(this.props.wizard.assembly.insertionLossSideA ||
                            this.props.wizard.assembly.insertionLossSideB ||
                            this.props.wizard.assembly.returnLossSideA ||
                            this.props.wizard.assembly.returnLossSideB ||
                            this.props.wizard.assembly.insertionLossTotal ||
                            this.props.wizard.assembly.returnLossTotal) && (
                            <tr>
                                <td>
                                    <div className="title">IL & RL</div>
                                </td>
                                <td>
                                    <ul>
                                        <li>
                                            <div className="flex">
                                                <div>Wavelengths:&nbsp; </div>
                                                <div style={{ fontStyle: 'italic', color: 'blue' }}>
                                                    {' '}
                                                    {this.props.wizard.wavelength_850 && '850'}{' '}
                                                    {this.props.wizard.wavelength_1300 && '1300'}{' '}
                                                    {this.props.wizard.wavelength_1310 && '1310'}{' '}
                                                    {this.props.wizard.wavelength_1490 && '1490'}{' '}
                                                    {this.props.wizard.wavelength_1550 && '1550'}{' '}
                                                    {this.props.wizard.wavelength_1625 && '1625'}{' '}
                                                </div>
                                            </div>
                                        </li>
                                        {(this.props.wizard.assembly.connectorEndA ||
                                            this.props.wizard.assembly.ilEndA ||
                                            this.props.wizard.assembly.rlEndA) && (
                                            <li>
                                                End A:&nbsp;
                                                <ul>
                                                    {this.props.wizard.assembly.connectorEndA && (
                                                        <li>
                                                            <div className="flex">
                                                                <div>Connector:&nbsp;</div>
                                                                <div style={{ fontStyle: 'italic', color: 'blue' }}>
                                                                    {this.props.wizard.assembly.connectorEndA}
                                                                </div>
                                                            </div>
                                                        </li>
                                                    )}
                                                    {this.props.wizard.assembly.ilEndA && (
                                                        <li>
                                                            <div className="flex">
                                                                <div>il &le;&nbsp;</div>
                                                                <div style={{ fontStyle: 'italic', color: 'blue' }}>
                                                                    {' '}
                                                                    {this.props.wizard.assembly.ilEndA}
                                                                </div>
                                                            </div>
                                                        </li>
                                                    )}
                                                    {this.props.wizard.assembly.rlEndA && (
                                                        <li>
                                                            <div className="flex">
                                                                <div>rl &ge;&nbsp;</div>
                                                                <div style={{ fontStyle: 'italic', color: 'blue' }}>
                                                                    {this.props.wizard.assembly.rlEndA}
                                                                </div>
                                                            </div>
                                                        </li>
                                                    )}
                                                </ul>
                                            </li>
                                        )}
                                        {(this.props.wizard.assembly.connectorEndB ||
                                            this.props.wizard.assembly.ilEndB ||
                                            this.props.wizard.assembly.rlEndB) && (
                                            <li>
                                                End B:&nbsp;
                                                <ul>
                                                    {this.props.wizard.assembly.connectorEndB && (
                                                        <li>
                                                            <div className="flex">
                                                                <div>Connector:&nbsp;</div>
                                                                <div style={{ fontStyle: 'italic', color: 'blue' }}>
                                                                    {this.props.wizard.assembly.connectorEndB}
                                                                </div>
                                                            </div>
                                                        </li>
                                                    )}
                                                    {this.props.wizard.assembly.ilEndB && (
                                                        <li>
                                                            <div className="flex">
                                                                <div>il &le;&nbsp;</div>
                                                                <div style={{ fontStyle: 'italic', color: 'blue' }}>
                                                                    {this.props.wizard.assembly.ilEndB}
                                                                </div>
                                                            </div>
                                                        </li>
                                                    )}
                                                    {this.props.wizard.assembly.rlEndB && (
                                                        <li>
                                                            <div className="flex">
                                                                <div>rl &ge;&nbsp;</div>
                                                                <div style={{ fontStyle: 'italic', color: 'blue' }}>
                                                                    {this.props.wizard.assembly.rlEndB}
                                                                </div>
                                                            </div>
                                                        </li>
                                                    )}
                                                </ul>
                                            </li>
                                        )}
                                        {this.props.wizard.assembly.rlTotal && (
                                            <li>
                                                <div className="flex">
                                                    <div>Rl Total &ge;&nbsp;</div>
                                                    <div style={{ fontStyle: 'italic', color: 'blue' }}>
                                                        {this.props.wizard.assembly.rlTotal}
                                                    </div>
                                                </div>
                                            </li>
                                        )}
                                        {this.props.wizard.assembly.ilTotal && (
                                            <li>
                                                <div className="flex">
                                                    <div>Il total &le;&nbsp;</div>
                                                    <div style={{ fontStyle: 'italic', color: 'blue' }}>
                                                        {this.props.wizard.assembly.ilTotal}
                                                    </div>
                                                </div>
                                            </li>
                                        )}
                                    </ul>
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
        );
    }
}
